<template>
  <div class="cb-app">
    <router-view v-if="phase === 'ready'" :key="$route.name === 'game' ? $route.query.round || 'game' : undefined" />

    <div v-else-if="phase === 'login'" class="cb-page app-auth">
      <AuthCard :notice="authNotice" :retryable="canRetryTelegram" @retry="authenticate" @authenticated="finishAuth" />
    </div>

    <div v-else class="cb-page app-splash" aria-busy="true">
      <GlowRing :size="180" :rings="3" arc spin>
        <span class="app-splash__logo"><AppIcon name="eye" :size="38" /></span>
      </GlowRing>
      <h1 class="app-splash__title">Гляделки</h1>
      <p class="app-splash__hint">{{ splashText }}</p>
    </div>

    <Transition name="cb-fade">
      <div v-if="showReconnectBanner" class="app-reconnect" role="status">
        <span class="cb-spinner" aria-hidden="true"></span>
        Переподключение…
      </div>
    </Transition>

    <ToastHost />

    <div v-if="rotateNeeded" class="app-rotate" role="alert">
      <AppIcon name="refresh" :size="44" />
      <p>Поверните телефон вертикально</p>
    </div>
  </div>
</template>

<script setup>
/**
 * Корень приложения: авторизация (Telegram initData → /auth/telegram, иначе форма логина),
 * подключение realtime, загрузка профиля, тосты, баннер переподключения, оверлей альбомной ориентации.
 */
import diagnostics from "@/services/diagnostics";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AuthCard from "@/components/AuthCard.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import ToastHost from "@/components/ui/ToastHost.vue";
import { telegramAuth, credentialsAuth } from "@/services/api";
import { preloadFaceDetectionCode } from "@/services/faceDetection";
import { prepareRoundAudio, refreshRoundAudio, stopInterfaceSound } from "@/services/roundSounds";
import realtime from "@/services/realtime";
import telegram from "@/services/telegram";
import { useSessionStore } from "@/stores/session";
import { useRoomStore } from "@/stores/room";
import { useGameStore } from "@/stores/game";
import { useToast } from "@/composables/useToast";

const MOCK_API = process.env.VUE_APP_MOCK_API === "true";
const LEGACY_KEYS = ["token", "isInitiator", "gameUUID"];
/** Через столько после входа подтягиваем JS-код распознавания лица (не мешая загрузке профиля). */
const FACE_CODE_PRELOAD_MS = 1000;
/** Телефон: короткая сторона экрана не больше (CSS px). */
const PHONE_SHORT_SIDE = 520;

const session = useSessionStore();
const room = useRoomStore();
room.captureLaunchIntent({ search: window.location.search, initData: telegram.initData(), startParam: telegram.startParam() });
const releaseRoundAudio = prepareRoundAudio(() => session.notificationsEnabled);
watch(() => session.notificationsEnabled, refreshRoundAudio, { flush: "sync" });
watch(() => realtime.status.value, (status) => {
  if (status !== "open") stopInterfaceSound("found");
}, { flush: "sync" });
const game = useGameStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();

/** 'loading' | 'login' | 'ready' */
const phase = ref("loading");
const splashText = ref("Входим…");
const authNotice = ref("");

const canRetryTelegram = computed(() => !!telegram.initData());
const showReconnectBanner = computed(() => phase.value === "ready" && realtime.status.value === "reconnecting");

function wsUrl() {
  if (process.env.VUE_APP_SERVER_WS) return process.env.VUE_APP_SERVER_WS;
  const origin = window.location.origin.replace(/^http/, "ws");
  return `${origin}/bunker/ws`;
}

async function finishAuth(token) {
  session.setToken(token);
  if (room.inviteToken) await router.replace({ path: "/room", query: { invite: room.inviteToken } });
  realtime.connect(wsUrl(), token);
  phase.value = "loading";
  splashText.value = "Загружаем профиль…";
  try {
    await session.loadMe();
  } catch (error) {
    if (error && error.status !== 401) toast.error("Не удалось загрузить профиль");
  }
  // Условие литеральное, чтобы webpack вырезал dev-чанк из прод-сборки.
  if (process.env.VUE_APP_MOCK_API === "true") {
    const { applyPreview } = await import(/* webpackChunkName: "dev-mock" */ "@/dev/mockApi");
    await applyPreview(router);
  }
  phase.value = "ready";
  // Чанк MediaPipe грузится лениво; держим его в памяти с начала сессии, чтобы деплой новой версии
  // (старый хэш чанка удалён с сервера) не ломал первую игру долгоживущей сессии.
  setTimeout(preloadFaceDetectionCode, FACE_CODE_PRELOAD_MS);
}

async function authenticate() {
  authNotice.value = "";
  const initData = telegram.initData();
  if (initData) {
    phase.value = "loading";
    splashText.value = "Входим через Telegram…";
    try {
      const token = await telegramAuth(initData);
      if (!token || typeof token !== "string") throw new Error("empty token");
      await finishAuth(token);
    } catch (error) {
      const reason = error && error.status === 0 ? ` ${error.message}` : "";
      authNotice.value = `Не удалось войти через Telegram.${reason}`;
      phase.value = "login";
    }
    return;
  }
  if (MOCK_API && !new URLSearchParams(window.location.search).has("login")) {
    // Мок-режим: входим автоматически (форму можно увидеть по ?login).
    await finishAuth(await credentialsAuth("demo", "demo"));
    return;
  }
  phase.value = "login";
}

// Просроченный JWT: сервер отклоняет WS-рукопожатие (401), но браузер видит только код 1006 и
// переподключается впустую. Если за PROBE_DELAY_MS соединение не вернулось — один раз за обрыв
// проверяем токен REST-запросом: 401 закроет WebSocket и откроет «Сессия истекла» (services/api.js).
const PROBE_DELAY_MS = 5000;
let probeScheduled = false;
realtime.on("open", () => {
  probeScheduled = false;
});
realtime.on("disconnected", () => {
  if (probeScheduled || phase.value !== "ready") return;
  probeScheduled = true;
  setTimeout(() => {
    if (realtime.status.value !== "open" && realtime.status.value !== "closed") session.loadMe().catch(() => null);
  }, PROBE_DELAY_MS);
});

/* ----------------------------------------------------- оверлей «Поверните телефон» */

// Решает физическая ориентация устройства, а не соотношение сторон окна: на Android клавиатура сжимает
// WebView, и «альбомный» по размерам вьюпорт закрыл бы поле ввода. Окно при этом тоже должно быть
// альбомным (страховка от неверного screen.orientation, например в эмуляции). Пока в поле ввода фокус —
// оверлей не показываем.
const rotateNeeded = ref(false);
const NON_TEXT_INPUTS = ["button", "checkbox", "color", "file", "image", "radio", "range", "reset", "submit"];

function physicalLandscape() {
  const orientation = window.screen && window.screen.orientation;
  if (orientation && typeof orientation.type === "string") return orientation.type.startsWith("landscape");
  if (typeof window.orientation === "number") return Math.abs(window.orientation) === 90;
  return false;
}

function isPhone() {
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const screen = window.screen;
  const shortSide = screen ? Math.min(screen.width, screen.height) : 0;
  return coarse && shortSide > 0 && shortSide <= PHONE_SHORT_SIDE;
}

function editingText() {
  const el = document.activeElement;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  return el.tagName === "INPUT" && !NON_TEXT_INPUTS.includes(String(el.type || "text").toLowerCase());
}

function updateRotate() {
  const wideWindow = window.innerWidth > window.innerHeight;
  rotateNeeded.value = isPhone() && physicalLandscape() && wideWindow && !editingText();
}

// focusout: activeElement обновится после события — проверяем в следующей задаче.
function onFocusOut() {
  setTimeout(updateRotate, 0);
}

function listenOrientation(add) {
  const method = add ? "addEventListener" : "removeEventListener";
  const orientation = window.screen && window.screen.orientation;
  if (orientation && typeof orientation[method] === "function") orientation[method]("change", updateRotate);
  window[method]("orientationchange", updateRotate);
  window[method]("resize", updateRotate);
  document[method]("focusin", updateRotate);
  document[method]("focusout", onFocusOut);
}

// Выход на главный экран завершает игровую сессию в сторе. flush:'post' — после размонтирования
// предыдущего экрана, чтобы его cleanup успел прочитать game.uuid.
watch(
  () => route.path,
  (path) => {
    if (!["/search", "/game"].includes(path)) stopInterfaceSound("found");
    if (path === "/" && game.uuid) game.reset();
  },
  { flush: "post" },
);

onMounted(() => {
  // Токен и старые игровые ключи сбрасываем при каждом старте; режим/соглашение/уведомления — не трогаем.
  LEGACY_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // хранилище недоступно
    }
  });
  telegram.init();
  updateRotate();
  listenOrientation(true);
  authenticate();
});

onBeforeUnmount(() => {
  diagnostics.stop();
  releaseRoundAudio();
  listenOrientation(false);
});
</script>

<style>
.app-splash,
.app-auth {
  align-items: center;
  justify-content: center;
}

.app-auth {
  justify-content: safe center;
}

.app-splash__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: var(--cb-surface-2);
  border: 1px solid var(--cb-lime-border);
  color: var(--cb-lime);
  box-shadow: var(--cb-glow);
}

.app-splash__title {
  margin-top: 12px;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.app-splash__hint {
  color: var(--cb-text-2);
}

.app-reconnect {
  position: fixed;
  top: calc(var(--cb-safe-top) + 10px);
  left: 50%;
  transform: translateX(-50%);
  z-index: calc(var(--cb-z-toast) - 1);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-radius: 999px;
  background: rgba(24, 29, 22, 0.94);
  border: 1px solid var(--cb-lime-border);
  box-shadow: var(--cb-glow-sm);
  color: var(--cb-text);
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
}

/* Оверлей «Поверните телефон» — телефон физически в альбомной ориентации (решает App.vue, см. rotateNeeded). */
.app-rotate {
  position: fixed;
  inset: 0;
  z-index: calc(var(--cb-z-critical) + 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  background: var(--cb-bg);
  color: var(--cb-lime);
  text-align: center;
}

.app-rotate p {
  color: var(--cb-text);
  font-size: 20px;
  font-weight: 700;
}
</style>
